/**
 * Grand Quack Auto — NPC system.
 *
 * Spawns pedestrian NPCs on sidewalk waypoint paths and runs their state
 * machines each tick. Exports scareNearbyNPCs() for the quack system, and
 * re-exports triggerRagdollLaunch() (Phase B2) so callers don't have to know
 * the ragdoll lives in a sibling module.
 *
 * State machine: WALKING → IDLE → FLEEING → RAGDOLL → WALKING.
 * Chain panic: scared NPCs trigger others within panicChainRadius (one level deep).
 *
 * Ragdoll integration arithmetic lives in npcRagdoll.ts (200-line cap split).
 *
 * See research.md §3 "NPC system: state machine, not pathfinding".
 */

import * as THREE from 'three';
import { NPC } from '../config';
import { createPedestrian, type PedestrianEntity } from '../entities/pedestrian';
import type { System, World } from '../types';
import { sidewalkPaths } from '../world/cityGenerator';
import { showSpeechBubble } from '../ui/speechBubbles';
import { addChaos } from './chaosSystem';
import {
  clearRagdollState,
  triggerRagdollLaunch as _triggerRagdollLaunch,
  updateRagdollNpc,
} from './npcRagdoll';

// Module state — NPCs are tracked here, not on World.
const npcs: PedestrianEntity[] = [];
let initialized = false;
let totalScared = 0;

/** Total NPCs scared across the entire session. Used by objectiveSystem. */
export function getTotalScared(): number { return totalScared; }

/** Read-only access to live NPCs. Used by tutorialSystem for proximity checks. */
export function getActiveNpcs(): ReadonlyArray<PedestrianEntity> { return npcs; }

// Reusable vectors to avoid per-frame allocation.
const _toWaypoint = new THREE.Vector3();

/**
 * Send every non-ragdolling NPC inside `radius` flying with a radial
 * blast + upward arc + tumble. Re-exported from npcRagdoll so callers
 * (quackSystem) can keep importing from npcSystem.
 */
export function triggerRagdollLaunch(
  world: World,
  origin: THREE.Vector3,
  radius: number,
): PedestrianEntity[] {
  return _triggerRagdollLaunch(world, origin, radius, npcs);
}

function initNpcs(world: World): void {
  initialized = true;
  if (sidewalkPaths.length === 0) return;

  const count = Math.min(NPC.maxActive, sidewalkPaths.length);
  for (let i = 0; i < count; i++) {
    const pathIndex = i % sidewalkPaths.length;
    npcs.push(createPedestrian(world, pathIndex));
  }
}

/** Snap NPC to the nearest waypoint on its assigned path. */
function snapToNearestWaypoint(npc: PedestrianEntity): void {
  const path = sidewalkPaths[npc.pathIndex];
  if (!path) return;
  let bestDist = Infinity;
  let bestIdx = 0;
  for (let i = 0; i < path.length; i++) {
    const d = npc.group.position.distanceToSquared(path[i]!);
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  }
  npc.waypointIndex = bestIdx;
}

/** Flip an NPC to the FLEEING state. */
function triggerFlee(npc: PedestrianEntity, awayFrom: THREE.Vector3): void {
  npc.state = 'fleeing';
  npc.stateTimer = NPC.fleeDurationSeconds;
  npc.fleeDirection.copy(npc.group.position).sub(awayFrom);
  npc.fleeDirection.y = 0;
  if (npc.fleeDirection.lengthSq() < 0.001) {
    npc.fleeDirection.set(Math.random() - 0.5, 0, Math.random() - 0.5);
  }
  npc.fleeDirection.normalize();
}

/**
 * Scare all NPCs within radius of origin. Called by quackSystem.
 * Chain panic: each newly scared NPC triggers others within panicChainRadius
 * (one level deep to prevent exponential cascade).
 */
export function scareNearbyNPCs(world: World, origin: THREE.Vector3, radius: number): void {
  const scared: PedestrianEntity[] = [];

  for (const npc of npcs) {
    if (npc.state === 'fleeing' || npc.state === 'ragdoll') continue;
    if (npc.group.position.distanceTo(origin) > radius) continue;
    triggerFlee(npc, origin);
    addChaos(world, 'scare');
    totalScared++;
    showSpeechBubble(world, npc.group.position, 'scared');
    scared.push(npc);
  }

  // Chain panic — one level deep only.
  for (const source of scared) {
    for (const npc of npcs) {
      if (npc.state === 'fleeing' || npc.state === 'ragdoll') continue;
      if (npc.group.position.distanceTo(source.group.position) > NPC.panicChainRadius) continue;
      triggerFlee(npc, source.group.position);
      addChaos(world, 'scare');
      totalScared++;
      showSpeechBubble(world, npc.group.position, 'scared');
    }
  }
}

export const npcSystem: System = {
  name: 'npc',
  fixedUpdate(dt: number, world: World) {
    if (!initialized) initNpcs(world);

    const duckPos = world.duck.position;

    for (const npc of npcs) {
      // Distance culling — freeze NPCs far from the player.
      if (npc.group.position.distanceTo(duckPos) > NPC.simulationRadius) continue;

      const path = sidewalkPaths[npc.pathIndex];
      if (!path || path.length === 0) continue;

      switch (npc.state) {
        case 'walking': {
          const target = path[npc.waypointIndex]!;
          _toWaypoint.copy(target).sub(npc.group.position);
          _toWaypoint.y = 0;
          const dist = _toWaypoint.length();

          if (dist > 0.5) {
            _toWaypoint.normalize();
            npc.group.position.addScaledVector(_toWaypoint, NPC.walkSpeed * dt);
            npc.group.rotation.y = Math.atan2(_toWaypoint.x, _toWaypoint.z);
          } else {
            // Reached waypoint — advance.
            npc.waypointIndex = (npc.waypointIndex + 1) % path.length;
            // 20% chance to pause.
            if (Math.random() < 0.2) {
              npc.state = 'idle';
              npc.stateTimer = 1 + Math.random() * 2;
            }
          }
          break;
        }

        case 'idle':
          npc.stateTimer -= dt;
          if (npc.stateTimer <= 0) npc.state = 'walking';
          break;

        case 'fleeing': {
          const fleeSpeed = NPC.walkSpeed * NPC.fleeSpeedMultiplier;
          npc.group.position.addScaledVector(npc.fleeDirection, fleeSpeed * dt);
          npc.group.rotation.y = Math.atan2(npc.fleeDirection.x, npc.fleeDirection.z);
          npc.stateTimer -= dt;
          if (npc.stateTimer <= 0) {
            npc.state = 'walking';
            snapToNearestWaypoint(npc);
          }
          break;
        }

        case 'ragdoll': {
          if (updateRagdollNpc(npc, dt)) {
            clearRagdollState(npc);
            snapToNearestWaypoint(npc);
            npc.state = 'walking';
          }
          break;
        }
      }
    }
  },
};
